/**
 * Calcolo ore e carichi di lavoro per medico
 *
 * SISTEMA UNIFICATO ORE E GIORNATE (Carichi, Piano di Lavoro, PDF):
 * - Ore slot: slot.hours ?? getHoursForSlot(timeSlot) → M/P 6h, Pr 7,5h, N 12h, Rep 12h
 * - Reperibilità: escluse da ore totali, da giorni lavorati e dal conteggio slot settimanali (max 6)
 * - Assenze accreditate (CO/Permesso/Rischio/Malattia: 7,6h solo lun–ven non festivo; sab/dom e festivi infrasettimanali no ore; formazione multi-giorno o intera: 7,6h per ogni giorno calendario): aggiunte a ore e giorni
 * - Turni su festivi infrasettimanali: contano come gli altri slot (ore e giorni)
 * - Giorni: giorni distinti con turno (notte conta anche giorno dopo); rep escluse
 * - Notte domenica: 4h + 1 giorno nella settimana ISO corrente (dom 20-24), 8h + 1 giorno nella successiva (lun 0-8)
 */

import {
  getState,
  getCreditedAbsenceHoursForRange,
  getFullDayAbsenceDates,
  getHoursLostForAvailability,
  getHoursPerDayForDoctorOnDate,
  isWeekLocked,
  saveWorkloadToHistory,
  deleteWorkloadMonthFromHistory,
} from './store.js';
import { getHoursForSlot, getSlotWeight, HOURS_PER_SLOT, DEFAULT_WEEKLY_HOURS, HOURS_PER_DAY, HOURS_NIGHT_START_DAY } from './types.js';
import { getOfficialSlotsForDates } from './shiftGenerator.js';
import {
  getFestiviInfrasettimanali,
  getISOWeekString,
  getDaysInWeekRange,
  getWeekDates,
  addCalendarDays,
  slotLogicalKeyString,
} from './utils.js';

/** Mappa chiave logica cella → doctorId dalle sole righe DB (stessa semantica di deduplica Piano). */
function buildWorkloadAssignmentByLogicalKey() {
  const { shiftSlots = [], shiftAssignments = {}, shiftAssignmentsByKey = {} } = getState();
  const map = Object.create(null);
  for (const s of shiftSlots) {
    const k = s.slotKey || s.id;
    const docId = (k && shiftAssignmentsByKey[k]) || (s.id && shiftAssignments[s.id]);
    if (!docId) continue;
    const lk = slotLogicalKeyString(s);
    if (lk) map[lk] = docId;
  }
  return map;
}

/**
 * Medico assegnato allo slot per Carichi / card Ore-Giorni: stesso ordine del Piano turni
 * (resolveAssignmentForSlot): prima shiftAssignmentsByKey, poi riga DB per id, poi cella logica.
 */
export function getDoctorIdAssignedToWorkloadSlot(slot, assignmentsByKey, assignmentByLogicalKey) {
  const byKey = assignmentsByKey ?? getState().shiftAssignmentsByKey ?? {};
  const k = slot?.slotKey || slot?.id;
  if (k && byKey[k]) return byKey[k];
  const shiftAssignments = getState().shiftAssignments || {};
  if (slot?.id && shiftAssignments[slot.id]) return shiftAssignments[slot.id];
  const lk = slotLogicalKeyString(slot);
  if (lk && assignmentByLogicalKey && assignmentByLogicalKey[lk]) return assignmentByLogicalKey[lk];
  return undefined;
}

/** Lucchetto su slot: per id riga DB o per chiave esatta (come shiftAssignmentsByKey). */
export function isWorkloadSlotLocked(slot, lockedByKey) {
  const { lockedAssignments = {} } = getState();
  if (slot?.id && lockedAssignments[slot.id]) return true;
  const k = slot?.slotKey || slot?.id;
  return Boolean(lockedByKey && k && lockedByKey[k]);
}

/** Date YYYY-MM-DD del mese solare (month 1–12). */
function getCalendarMonthDateStrings(year, month) {
  const lastDay = new Date(year, month, 0).getDate();
  const ym = `${year}-${String(month).padStart(2, '0')}-`;
  const dates = [];
  for (let day = 1; day <= lastDay; day++) {
    dates.push(`${ym}${String(day).padStart(2, '0')}`);
  }
  return dates;
}

/**
 * Il Direttore (abilitaDirezione) sta fuori dai conti sul monte orario: fa solo turni Direzione,
 * sempre gli stessi, quindi un saldo rispetto al debito settimanale non descrive nulla di reale.
 * Il solver lo esclude già dai pool di bilanciamento (is_director_balance_excluded).
 */
export function isExcludedFromWorkloadBalance(doctor) {
  return Boolean(doctor?.abilitaDirezione ?? doctor?.abilita_direzione);
}

/** Elenco medici al netto di chi non partecipa al bilanciamento del monte orario. */
export function doctorsForWorkloadBalance(doctors) {
  return (doctors ?? []).filter(d => d && !isExcludedFromWorkloadBalance(d));
}

/** Carichi — ore dovute per giorno feriale da debito orario settimanale (debito ÷ 5), come store/solver. */
function getExpectedHoursPerFerialeDayForCarichi(doctorId, referenceDateStr) {
  return getHoursPerDayForDoctorOnDate(doctorId, referenceDateStr);
}

/** Ore e peso slot sono su ogni slot (.hours, .slotWeight): usare quelli per conteggi univoci. Reperibilità = 12h (giorno e notte). */
function slotHours(slot) {
  const isRep = slot.activityType === 'reperibilita' || slot.timeSlot === 'reperibilita_notte' || slot.timeSlot === 'reperibilita_giorno' || slot.timeSlot === 'reperibilita' || slot.timeSlot === 'reperibilita';
  if (isRep) return slot.hours ?? 12;
  return slot.hours ?? getHoursForSlot(slot.timeSlot);
}

/** Slot turno Direzione (categoria / tipo attività / nome standard "Direzione"): escluso da domanda e disponibilità del bilancio settimanale. */
function isDirezioneSlotForWorkload(slot) {
  if (!slot) return false;
  const c = String(slot.category ?? '').trim().toLowerCase();
  const at = String(slot.activityType ?? slot.activity_type ?? '').trim().toLowerCase();
  if (c === 'direzione' || at === 'direzione') return true;
  return String(slot.name ?? '').trim().toLowerCase() === 'direzione';
}

/** Peso slot nella stima preventiva: M/P=1, Pr=1,2, N=2, Rep=0 (come getSlotWeight). Ogni unità di slot vale HOURS_PER_SLOT (6h). */
function preventiveWorkloadSlotWeight(slot) {
  return slot.slotWeight ?? getSlotWeight(slot.timeSlot);
}

/** Ripartizione di un turno notte (20-8) fra i due giorni di calendario che attraversa: 20-24 = 4h sul giorno
 *  di inizio, 0-8 = 8h sul giorno successivo. Per l'aggregazione per settimana ISO la ripartizione è visibile
 *  solo quando il turno scavalca la settimana, cioè per la notte di domenica: 4h nella settimana corrente,
 *  8h nella successiva. Negli altri giorni entrambe le quote cadono nella stessa settimana. */
export function getNightHoursSplit(slot) {
  const h = slotHours(slot);
  if (slot.timeSlot === 'notte' && slot.weekday === 'dom') {
    const currentWeek = Math.min(HOURS_NIGHT_START_DAY, h);
    return { currentWeek, nextWeek: h - currentWeek };
  }
  return { currentWeek: h, nextWeek: 0 };
}

/** Restituisce il lunedì della settimana ISO (YYYY-MM-DD) per una data. Settimana ISO: lun-dom. */
export function getWeekStart(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const daysToMonday = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - daysToMonday);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Restituisce la domenica della settimana */
export function getWeekEnd(dateStr) {
  const start = new Date(getWeekStart(dateStr) + 'T12:00:00');
  start.setDate(start.getDate() + 6);
  return `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
}

/** Numero festivi infrasettimanali in una settimana (lun-dom). Solo giorni feriali, non sab/dom. */
export function getFestiviCountInWeek(weekStart, year) {
  let count = 0;
  const yearsInWeek = new Set();
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() + i);
    yearsInWeek.add(d.getFullYear());
  }
  const festiviByYear = {};
  for (const y of yearsInWeek) festiviByYear[y] = getFestiviInfrasettimanali(y);
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() + i);
    const y = d.getFullYear();
    const dateStr = `${y}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (festiviByYear[y].has(dateStr)) count++;
  }
  return count;
}

/** Calcola ore per medico in una settimana (lun-dom). Accrediti CO/Permesso solo feriali non festivi. Notte dom: 4h in settimana corrente, 8h nella successiva. */
export function computeDoctorHoursForWeek(slots, assignmentsByKey, dateInWeek) {
  const weekStart = getWeekStart(dateInWeek);
  const weekEnd = getWeekEnd(dateInWeek);
  const prevSunday = addCalendarDays(weekStart, -1); // domenica settimana precedente (notte dom → quota lun 0-8 in questa settimana)
  const [year] = dateInWeek.split('-').map(Number);
  const assignmentByLogicalKey = buildWorkloadAssignmentByLogicalKey();
  const hours = {};
  const addSlotHours = (docId, slot, h) => {
    if (!hours[docId]) hours[docId] = { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0, total: 0 };
    const isWeekend = ['sab', 'dom'].includes(slot.weekday);
    const isNight = slot.timeSlot === 'notte';
    hours[docId].total += h;
    if (isNight) hours[docId].notturne += h;
    if (isWeekend) hours[docId].weekend += h;
    if (!isWeekend && !isNight) hours[docId].feriali += h;
  };
  for (const slot of slots) {
    const docId = getDoctorIdAssignedToWorkloadSlot(slot, assignmentsByKey, assignmentByLogicalKey);
    if (!docId) continue;
    const isReperibilita = slot.activityType === 'reperibilita' || slot.timeSlot === 'reperibilita_notte' || slot.timeSlot === 'reperibilita_giorno' || slot.timeSlot === 'reperibilita';
    // Notte domenica precedente: quota lun 0-8 (8h) in questa settimana, non weekend
    if (slot.date === prevSunday && slot.timeSlot === 'notte' && slot.weekday === 'dom') {
      if (!hours[docId]) hours[docId] = { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0, total: 0 };
      const { nextWeek: hSpill } = getNightHoursSplit(slot);
      hours[docId].total += hSpill;
      hours[docId].notturne += hSpill;
      continue;
    }
    if (slot.date < weekStart || slot.date > weekEnd) continue;
    if (isReperibilita) {
      if (!hours[docId]) hours[docId] = { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0, total: 0 };
      hours[docId].reperibilita += slotHours(slot);
    } else {
      const { currentWeek: h } = getNightHoursSplit(slot);
      addSlotHours(docId, slot, h);
    }
  }
  // Aggiungi ore accreditate (Congedo/Permesso/Rischio 7,6h/giorno feriale non festivo; formazione multi-giorno o intera 7,6h/giorno calendario). Un solo accredito per giorno (no doppio conteggio).
  const { doctors } = getState();
  for (const doc of doctors) {
    const cred = getCreditedAbsenceHoursForRange(doc.id, weekStart, weekEnd);
    if (cred > 0) {
      if (!hours[doc.id]) hours[doc.id] = { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0, total: 0 };
      hours[doc.id].total += cred;
      hours[doc.id].feriali += cred;
    }
  }
  return hours;
}

/**
 * Spread clinico ISO globale post-WE+N (nessuna infra): baseline per il solver infra.
 * assignmentsAfterLockedPhases = debtAssignments (weekend, notti, preassegnati lucchettati).
 */
export function computeSpreadBaselineForPeriod(clinicalSlots, assignmentsAfterLockedPhases, periodStart, periodEnd) {
  const { doctors } = getState();
  const weekStarts = getWeekStartsInRange(periodStart, periodEnd);
  const expanded = { ...(assignmentsAfterLockedPhases || {}) };
  for (const s of clinicalSlots || []) {
    const keys = [s.slotKey, s.id].filter(Boolean);
    let docId = null;
    for (const k of keys) {
      if (expanded[k]) {
        docId = expanded[k];
        break;
      }
    }
    const lk = slotLogicalKeyString(s);
    if (!docId && lk && expanded[lk]) docId = expanded[lk];
    if (!docId) continue;
    for (const k of keys) expanded[k] = docId;
    if (lk) expanded[lk] = docId;
  }
  const spreadBaselineByWeek = {};
  const spreadFloorByWeek = {};
  for (const weekStart of weekStarts) {
    const weekEnd = getWeekEnd(weekStart);
    const [year] = weekStart.split('-').map(Number);
    const festiviCount = getFestiviCountInWeek(weekStart, year);
    const workingDays = Math.max(0, 5 - festiviCount);
    const doctorsMap = {};
    const inPistaTotals = [];
    const hours = computeDoctorHoursForWeek(clinicalSlots, expanded, weekStart);
    for (const doc of doctors) {
      const fullDay = getFullDayAbsenceDates(doc.id, weekStart, weekEnd);
      const inPista = workingDays > 0 && fullDay.size < workingDays;
      const total = hours[doc.id]?.total ?? 0;
      doctorsMap[doc.id] = { hoursAfterLockedPhases: total, inPista };
      if (inPista) inPistaTotals.push(total);
    }
    let floorHours = 0;
    let minHours = 0;
    let maxHours = 0;
    if (inPistaTotals.length >= 2) {
      minHours = Math.min(...inPistaTotals);
      maxHours = Math.max(...inPistaTotals);
      floorHours = maxHours - minHours;
    }
    spreadBaselineByWeek[weekStart] = { doctors: doctorsMap };
    spreadFloorByWeek[weekStart] = {
      floorHours,
      minHours,
      maxHours,
      poolSize: inPistaTotals.length,
    };
  }
  return { spreadBaselineByWeek, spreadFloorByWeek };
}

/** Sabato o domenica per la data indicata (classificazione weekend basata sul giorno realmente lavorato). */
function isWeekendDateString(dateStr) {
  const wd = new Date(dateStr + 'T12:00:00').getDay();
  return wd === 0 || wd === 6;
}

/**
 * Quote orarie di uno slot per data di calendario: la notte 20-8 vale 4h sul giorno di inizio
 * (20-24) e 8h sul giorno successivo (0-8); ogni altro slot vale tutto sul suo giorno.
 * Serve a chi aggrega su intervalli di calendario (mese, bimestre): un turno a cavallo del
 * confine entra solo per la quota che ci ricade davvero.
 * @returns {Array<[string, number]>} coppie [data, ore]
 */
export function slotHourPartsByDate(slot) {
  const ds = (slot.date || '').slice(0, 10);
  const h = slotHours(slot);
  if (!ds || h <= 0) return [];
  if (slot.timeSlot !== 'notte') return [[ds, h]];
  const first = Math.min(HOURS_NIGHT_START_DAY, h);
  return [[ds, first], [addCalendarDays(ds, 1), h - first]];
}

/** Ore per medico nel mese solare, attribuite per data di calendario. Include ore formazione.
 *  Le notti a cavallo del confine di mese entrano solo con la quota di competenza (4h/8h),
 *  cosi' la somma dei due mesi di un bimestre coincide con il totale del bimestre. */
export function computeDoctorHoursForMonth(year, month) {
  const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
  const monthEnd = `${year}-${String(month).padStart(2, '0')}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`;
  // Il giorno precedente serve per la quota 0-8 di una notte iniziata l'ultimo giorno del mese prima.
  const dayBefore = addCalendarDays(monthStart, -1);
  const slots = getOfficialSlotsForDates([dayBefore, ...getCalendarMonthDateStrings(year, month)]);
  const { shiftAssignmentsByKey, doctors } = getState();
  const byKey = shiftAssignmentsByKey || {};
  const assignmentByLogicalKey = buildWorkloadAssignmentByLogicalKey();
  const hours = {};
  const ensure = (docId) => {
    if (!hours[docId]) hours[docId] = { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0, total: 0 };
    return hours[docId];
  };
  for (const slot of slots) {
    const docId = getDoctorIdAssignedToWorkloadSlot(slot, byKey, assignmentByLogicalKey);
    if (!docId) continue;
    const ds = (slot.date || '').slice(0, 10);
    if (!ds) continue;
    const isReperibilita = slot.activityType === 'reperibilita' || slot.timeSlot === 'reperibilita_notte' || slot.timeSlot === 'reperibilita_giorno' || slot.timeSlot === 'reperibilita';
    if (isReperibilita) {
      // Fuori dal monte orario: resta attribuita per intero al giorno di inizio.
      if (ds >= monthStart && ds <= monthEnd) ensure(docId).reperibilita += slotHours(slot);
      continue;
    }
    const isNight = slot.timeSlot === 'notte';
    for (const [partDate, partHours] of slotHourPartsByDate(slot)) {
      if (partHours <= 0) continue;
      if (partDate < monthStart || partDate > monthEnd) continue;
      const rec = ensure(docId);
      const isWeekend = isWeekendDateString(partDate);
      rec.total += partHours;
      if (isNight) rec.notturne += partHours;    // tutte le notti (infrasett. + weekend)
      if (isWeekend) rec.weekend += partHours;   // weekend del giorno realmente lavorato
      if (!isWeekend && !isNight) rec.feriali += partHours;
    }
  }
  const weekStarts = new Set();
  for (let day = 1; day <= new Date(year, month, 0).getDate(); day++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    weekStarts.add(getWeekStart(dateStr));
  }
  const weekEnds = {};
  for (const weekStart of weekStarts) {
    weekEnds[weekStart] = getWeekEnd(weekStart);
  }
  for (const weekStart of weekStarts) {
    const weekEnd = weekEnds[weekStart];
    const credStart = weekStart < monthStart ? monthStart : weekStart;
    const credEnd = weekEnd > monthEnd ? monthEnd : weekEnd;
    for (const doc of doctors) {
      const cred = credStart <= credEnd ? getCreditedAbsenceHoursForRange(doc.id, credStart, credEnd) : 0;
      if (cred > 0) {
        if (!hours[doc.id]) hours[doc.id] = { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0, total: 0 };
        hours[doc.id].total += cred;
        hours[doc.id].feriali += cred;
      }
    }
  }
  return hours;
}

/** Per ogni settimana (da slots), ore e giornate lavorate per medico. Ritorna { weekStart: { doctorId: { total, feriali, ..., days } } }. Include ore formazione.
 * days = numero di giorni distinti con almeno un turno (notte conta anche il giorno dopo per riposo). Le reperibilità non contano come giorno lavorato. */
export function computeWeeklyBreakdownFromSlots(slots, year, month) {
  const { shiftAssignmentsByKey, doctors } = getState();
  const byKey = shiftAssignmentsByKey || {};
  const assignmentByLogicalKey = buildWorkloadAssignmentByLogicalKey();
  const weeks = {};
  const daysByWeekDoc = {};
  const addDay = (weekStart, docId, dateStr) => {
    if (!daysByWeekDoc[weekStart]) daysByWeekDoc[weekStart] = {};
    if (!daysByWeekDoc[weekStart][docId]) daysByWeekDoc[weekStart][docId] = new Set();
    const weekEnd = getWeekEnd(weekStart);
    if (dateStr >= weekStart && dateStr <= weekEnd) daysByWeekDoc[weekStart][docId].add(dateStr);
  };
  for (const slot of slots) {
    const weekStart = getWeekStart(slot.date);
    if (!weeks[weekStart]) weeks[weekStart] = {};
    const docId = getDoctorIdAssignedToWorkloadSlot(slot, byKey, assignmentByLogicalKey);
    if (!docId) continue;
    const isReperibilita = slot.activityType === 'reperibilita' || slot.timeSlot === 'reperibilita_notte' || slot.timeSlot === 'reperibilita_giorno' || slot.timeSlot === 'reperibilita';
    if (!weeks[weekStart][docId]) weeks[weekStart][docId] = { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0, total: 0 };
    const isSundayNight = slot.timeSlot === 'notte' && slot.weekday === 'dom';
    const nextD = addCalendarDays(slot.date, 1);
    const nextWeekStart = getWeekStart(nextD);
    // Giorni lavorati: solo turni effettivi, non reperibilità
    if (!isReperibilita) {
      addDay(weekStart, docId, slot.date);
      if (slot.timeSlot === 'notte') {
        addDay(isSundayNight ? nextWeekStart : weekStart, docId, nextD);
      }
    }
    const { currentWeek: hCur, nextWeek: hNext } = getNightHoursSplit(slot);
    const isWeekend = ['sab', 'dom'].includes(slot.weekday);
    const isNight = slot.timeSlot === 'notte';
    if (isReperibilita) weeks[weekStart][docId].reperibilita += slotHours(slot);
    else {
      weeks[weekStart][docId].total += hCur;
      if (isNight) weeks[weekStart][docId].notturne += hCur;
      if (isWeekend) weeks[weekStart][docId].weekend += hCur;
      if (!isWeekend && !isNight) weeks[weekStart][docId].feriali += hCur;
      if (hNext > 0) {
        if (!weeks[nextWeekStart]) weeks[nextWeekStart] = {};
        if (!weeks[nextWeekStart][docId]) weeks[nextWeekStart][docId] = { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0, total: 0 };
        weeks[nextWeekStart][docId].total += hNext;
        weeks[nextWeekStart][docId].notturne += hNext; // lun 0-8 = parte turno notte dom, non weekend
      }
    }
  }
  for (const weekStart of Object.keys(daysByWeekDoc)) {
    for (const docId of Object.keys(daysByWeekDoc[weekStart])) {
      if (weeks[weekStart] && weeks[weekStart][docId]) {
        weeks[weekStart][docId].days = daysByWeekDoc[weekStart][docId].size;
      }
    }
  }
  const monthStart = year && month ? `${year}-${String(month).padStart(2, '0')}-01` : null;
  const monthEnd = year && month ? `${year}-${String(month).padStart(2, '0')}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}` : null;
  for (const weekStart of Object.keys(weeks)) {
    const weekEnd = getWeekEnd(weekStart);
    const credStart = monthStart && weekStart < monthStart ? monthStart : weekStart;
    const credEnd = monthEnd && weekEnd > monthEnd ? monthEnd : weekEnd;
    for (const doc of doctors) {
      const cred = credStart <= credEnd ? getCreditedAbsenceHoursForRange(doc.id, credStart, credEnd) : 0;
      if (cred > 0) {
        if (!weeks[weekStart][doc.id]) weeks[weekStart][doc.id] = { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0, total: 0 };
        weeks[weekStart][doc.id].total += cred;
        weeks[weekStart][doc.id].feriali += cred;
      }
      // Aggiungi giorni di assenza (permessi/congedi) al conteggio giorni lavorati
      const absences = getFullDayAbsenceDates(doc.id, weekStart, weekEnd);
      if (absences.size > 0) {
        if (!daysByWeekDoc[weekStart]) daysByWeekDoc[weekStart] = {};
        if (!daysByWeekDoc[weekStart][doc.id]) daysByWeekDoc[weekStart][doc.id] = new Set();
        for (const d of absences) daysByWeekDoc[weekStart][doc.id].add(d);
      }
      if (weeks[weekStart][doc.id]) {
        weeks[weekStart][doc.id].days = daysByWeekDoc[weekStart]?.[doc.id]?.size ?? 0;
      }
    }
  }
  return weeks;
}

/** Per ogni settimana del mese, ore per medico. Ritorna { weekStart: { doctorId: hours } }. Include ore formazione. */
export function computeWeeklyBreakdown(year, month) {
  const slots = getOfficialSlotsForDates(getCalendarMonthDateStrings(year, month));
  return computeWeeklyBreakdownFromSlots(slots, year, month);
}

/** Restituisce le settimane (lunedì YYYY-MM-DD) nell'intervallo [weekFrom, weekTo], ordinate. */
export function getWeekStartsInRange(weekFrom, weekTo) {
  const days = getDaysInWeekRange(weekFrom, weekTo);
  const weekStarts = new Set();
  for (const d of days) {
    weekStarts.add(getWeekStart(d.date));
  }
  return [...weekStarts].sort();
}

/** Carico (ore e giorni) già coperto da sole assegnazioni lucchettate. Ritorna { weekStart: { doctorId: { feriali, weekend, notturne, reperibilita, total, days } } }. Stessa logica di computeWeeklyBreakdownFromSlots ma solo per slot con lockedByKey[key]=true. */
export function computeLockedWorkloadByWeek(slots, assignmentsByKey, lockedByKey) {
  const assignmentByLogicalKey = buildWorkloadAssignmentByLogicalKey();
  const weeks = {};
  const daysByWeekDoc = {};
  const addDay = (weekStart, docId, dateStr) => {
    if (!daysByWeekDoc[weekStart]) daysByWeekDoc[weekStart] = {};
    if (!daysByWeekDoc[weekStart][docId]) daysByWeekDoc[weekStart][docId] = new Set();
    const weekEnd = getWeekEnd(weekStart);
    if (dateStr >= weekStart && dateStr <= weekEnd) daysByWeekDoc[weekStart][docId].add(dateStr);
  };
  for (const slot of slots) {
    const docId = getDoctorIdAssignedToWorkloadSlot(slot, assignmentsByKey, assignmentByLogicalKey);
    if (!docId || !isWorkloadSlotLocked(slot, lockedByKey)) continue;
    const weekStart = getWeekStart(slot.date);
    if (!weeks[weekStart]) weeks[weekStart] = {};
    const isReperibilita = slot.activityType === 'reperibilita' || slot.timeSlot === 'reperibilita_notte' || slot.timeSlot === 'reperibilita_giorno' || slot.timeSlot === 'reperibilita';
    if (!weeks[weekStart][docId]) weeks[weekStart][docId] = { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0, total: 0 };
    const isSundayNight = slot.timeSlot === 'notte' && slot.weekday === 'dom';
    const nextD = addCalendarDays(slot.date, 1);
    const nextWeekStart = getWeekStart(nextD);
    if (!isReperibilita) {
      addDay(weekStart, docId, slot.date);
      if (slot.timeSlot === 'notte') addDay(isSundayNight ? nextWeekStart : weekStart, docId, nextD);
    }
    const { currentWeek: hCur, nextWeek: hNext } = getNightHoursSplit(slot);
    const isWeekend = ['sab', 'dom'].includes(slot.weekday);
    const isNight = slot.timeSlot === 'notte';
    if (isReperibilita) weeks[weekStart][docId].reperibilita += slotHours(slot);
    else {
      weeks[weekStart][docId].total += hCur;
      if (isNight) weeks[weekStart][docId].notturne += hCur;
      if (isWeekend) weeks[weekStart][docId].weekend += hCur;
      if (!isWeekend && !isNight) weeks[weekStart][docId].feriali += hCur;
      if (hNext > 0) {
        if (!weeks[nextWeekStart]) weeks[nextWeekStart] = {};
        if (!weeks[nextWeekStart][docId]) weeks[nextWeekStart][docId] = { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0, total: 0 };
        weeks[nextWeekStart][docId].total += hNext;
        weeks[nextWeekStart][docId].notturne += hNext;
      }
    }
  }
  for (const weekStart of Object.keys(daysByWeekDoc)) {
    for (const docId of Object.keys(daysByWeekDoc[weekStart])) {
      if (weeks[weekStart] && weeks[weekStart][docId]) {
        weeks[weekStart][docId].days = daysByWeekDoc[weekStart][docId].size;
      }
    }
  }
  return weeks;
}

/**
 * Conteggio slot di lavoro (escluse reperibilità) già assegnati/lucchettati per medico e settimana.
 * Usato dal solver per max6 in modalità a fasi: solo slot lucchettati (o fasi precedenti già consolidate)
 * contano nel tetto di 6 slot/settimana anche se non sono nel modello della fase corrente.
 * Notte domenica: conta nella settimana del slot date (domenica), non nella successiva.
 * @param {object[]} slots - tutti gli slot del periodo
 * @param {object} assignmentsByKey - slotKey -> doctorId
 * @param {object} lockedByKey - slotKey -> true per slot lucchettati
 * @returns {object} { doctorId: { weekStart: count } }
 */
export function computeLockedWorkSlotsByDoctorWeek(slots, assignmentsByKey, lockedByKey) {
  const assignmentByLogicalKey = buildWorkloadAssignmentByLogicalKey();
  const result = {};
  for (const slot of slots) {
    const docId = getDoctorIdAssignedToWorkloadSlot(slot, assignmentsByKey, assignmentByLogicalKey);
    if (!docId || !isWorkloadSlotLocked(slot, lockedByKey)) continue;
    const isRep = slot.activityType === 'reperibilita' || (slot.timeSlot || '').startsWith('reperibilita');
    if (isRep) continue;
    const weekStart = getWeekStart(slot.date || '');
    if (!weekStart) continue;
    if (!result[docId]) result[docId] = {};
    result[docId][weekStart] = (result[docId][weekStart] || 0) + 1;
  }
  return result;
}

/**
 * Overview ore weekend per medico nel periodo, eventualmente esteso alle 4 settimane precedenti.
 * Usato per il bilanciamento weekend.
 * @param {object[]} slots - slot del periodo (o periodo + 4 sett. prec. se extendWeeks>0)
 * @param {object} assignmentsByKey - slotKey -> doctorId
 * @param {string} periodStart - inizio periodo
 * @param {string} periodEnd - fine periodo
 * @param {number} extendWeeks - settimane ISO da includere prima di periodStart (default 4)
 * @returns {object} { doctorId: totalWeekendHours }
 */
function saturdayKeyForWeekendSlot(slot) {
  const dateStr = (slot.date || '').slice(0, 10);
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00`);
  if (slot.weekday === 'dom') d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Presenze weekend storiche (sabato-chiave) per medico nelle settimane ISO prima di periodStart.
 * Usato dal CP weekend per penalizzare weekend consecutivi oltre il confine del periodo.
 * @returns {object} { doctorId: string[] } sabati YYYY-MM-DD ordinati in cui il medico ha coperto almeno uno slot WE
 */
export function computeWeekendPresenceHistoryByDoctor(
  slots,
  assignmentsByKey,
  periodStart,
  lookbackWeeks = 4,
) {
  const result = {};
  if (!periodStart) return result;
  const periodWs = getWeekStart(periodStart);
  const extendedStart = addCalendarDays(periodWs, -lookbackWeeks * 7);
  const assignmentByLogicalKey = buildWorkloadAssignmentByLogicalKey();
  for (const slot of slots || []) {
    const dateStr = (slot.date || '').slice(0, 10);
    if (!dateStr || dateStr < extendedStart) continue;
    const slotWs = getWeekStart(dateStr);
    if (slotWs >= periodWs) continue;
    if (!['sab', 'dom'].includes(slot.weekday || '')) continue;
    const isRep = slot.activityType === 'reperibilita' || (slot.timeSlot || '').startsWith('reperibilita');
    if (isRep) continue;
    const docId = getDoctorIdAssignedToWorkloadSlot(slot, assignmentsByKey, assignmentByLogicalKey);
    if (!docId) continue;
    const satKey = saturdayKeyForWeekendSlot(slot);
    if (!satKey) continue;
    if (!result[docId]) result[docId] = new Set();
    result[docId].add(satKey);
  }
  const out = {};
  for (const [docId, satSet] of Object.entries(result)) {
    out[docId] = [...satSet].sort();
  }
  return out;
}

export function computeWeekendHoursOverview(slots, assignmentsByKey, periodStart, periodEnd, extendWeeks = 4) {
  const result = {};
  if (!periodStart || !periodEnd) return result;
  const wsStart = getWeekStart(periodStart);
  const wsEnd = getWeekStart(periodEnd);
  const extendedStart = addCalendarDays(wsStart, -extendWeeks * 7);
  const assignmentByLogicalKey = buildWorkloadAssignmentByLogicalKey();
  for (const slot of slots) {
    const dateStr = (slot.date || '').slice(0, 10);
    if (!dateStr || dateStr < extendedStart || dateStr > periodEnd) continue;
    if (!['sab', 'dom'].includes(slot.weekday || '')) continue;
    const isRep = slot.activityType === 'reperibilita' || (slot.timeSlot || '').startsWith('reperibilita');
    if (isRep) continue;
    const docId = getDoctorIdAssignedToWorkloadSlot(slot, assignmentsByKey, assignmentByLogicalKey);
    if (!docId) continue;
    const h = slotHours(slot);
    if (!result[docId]) result[docId] = 0;
    result[docId] += h;
  }
  return result;
}

/** Calcola debito residuo per medico e settimana nel periodo (per solver). Ritorna { doctorResidualDebt: { doctorId: { weekStart: {...} } }, festiviInfrasettimanaliByWeek: { weekStart: count } }. */
export function computeDoctorResidualDebtForPeriod(slots, assignmentsByKey, lockedByKey, periodStart, periodEnd) {
  const { doctors } = getState();
  const weekStarts = getWeekStartsInRange(periodStart, periodEnd);
  const festiviInfrasettimanaliByWeek = {};
  for (const ws of weekStarts) {
    const [y] = ws.split('-').map(Number);
    festiviInfrasettimanaliByWeek[ws] = getFestiviCountInWeek(ws, y);
  }
  // Solo assegnazioni lucchettate (o settimana archiviata) contano come impegno fisso per cap ore/max6.
  // Le assegnazioni libere sono riscrivibili dal solver e non devono bloccare l'eleggibilità.
  const lockedWorkload = computeLockedWorkloadByWeek(slots, assignmentsByKey, lockedByKey || {});
  // Saldo bimestrale in ingresso a ogni settimana, dalle sole settimane archiviate: alimenta la
  // leva di preferenza del solver (un turno in più a chi è sotto traiettoria, uno in meno a chi è sopra).
  const bimesterBefore = computeLockedBimesterBalancesBeforeWeeks(weekStarts, doctors);
  const doctorResidualDebt = {};
  for (const doc of doctors) {
    const dataFine = doc.dataFineRapporto || null;
    doctorResidualDebt[doc.id] = {};
    for (const weekStart of weekStarts) {
      if (dataFine && weekStart > dataFine) continue;
      const [year] = weekStart.split('-').map(Number);
      const festiviCount = festiviInfrasettimanaliByWeek[weekStart] ?? getFestiviCountInWeek(weekStart, year);
      const workingDays = Math.max(0, 5 - festiviCount);
      const weekEnd = getWeekEnd(weekStart);
      const hoursPerDay = getHoursPerDayForDoctorOnDate(doc.id, weekStart);
      const creditedHours = getCreditedAbsenceHoursForRange(doc.id, weekStart, weekEnd);
      const targetHours = Math.max(0, workingDays * hoursPerDay - creditedHours);
      const fullDayInWeek = getFullDayAbsenceDates(doc.id, weekStart, weekEnd);
      const targetDays = Math.max(0, workingDays - fullDayInWeek.size);
      const locked = lockedWorkload[weekStart]?.[doc.id] || { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0, total: 0, days: 0 };
      const lockedNights = Math.round((locked.notturne || 0) / 12);
      const bimEntry = bimesterBefore[weekStart];
      const bim = bimEntry?.byDoctor?.[doc.id] || null;
      doctorResidualDebt[doc.id][weekStart] = {
        workingDays,
        festiviCount,
        targetHours,
        targetDays,
        creditedHours,
        lockedFeriali: locked.feriali || 0,
        lockedWeekend: locked.weekend || 0,
        lockedNotturne: locked.notturne || 0,
        lockedRep: locked.reperibilita || 0,
        lockedTotal: locked.total || 0,
        lockedDays: locked.days ?? 0,
        lockedNights,
        residualHours: Math.max(0, targetHours - (locked.total || 0)),
        residualDays: Math.max(0, targetDays - (locked.days ?? 0)),
        residualNights: 0,
        residualWeekend: 0,
        residualRep: 0,
        // Il solver distingue le settimane gia' archiviate (dentro bimesterSaldoLocked) da quelle
        // ancora aperte dell'intervallo, che conta invece sullo stato corrente del piano.
        weekLocked: Boolean(isWeekLocked(weekStart)),
        bimesterKey: bimEntry?.range?.key ?? null,
        bimesterSaldoLocked: bim ? bim.saldo : 0,
        bimesterLockedWeeks: bim ? bim.settimaneArchiviate : 0,
        bimesterBias: bim ? bimesterPreferenceBias(bim.saldo, bim.dovute) : 0,
      };
    }
  }
  return { doctorResidualDebt, festiviInfrasettimanaliByWeek };
}

/**
 * Ripartizione aritmetica: carico target weekend, notti e reperibilità per medico nel periodo.
 * Weekend: factor = 1 - (giorni weekend in assenza / giorni weekend totali). Notti/Rep: factor = 1 - assenze/giorniPeriodo.
 * eligibleWeekendIds: tutti i medici eleggibili al weekend (non specializzandi, non direttore, non esenzione weekend).
 * @param {object} opts - slotWEPeriodo, weekendCountPeriodo, weekendNightsPeriodo, nottiPeriodo, nottiFerialiPeriodo, nottiWeeksPeriodo, repPeriodo, giorniPeriodo, doctorIds, absenceDaysInPeriodByDoctor, weekendDaysInPeriod, weekendDaysAbsentByDoctor, eligibleWeekendIds, eligibleNightIds, eligibleRepIds, lockedWeekendByDoctor, lockedWeekendCountByDoctor, lockedWeekendNightsByDoctor, lockedNightsByDoctor, lockedNightsFerialiByDoctor, lockedRepByDoctor
 * @returns {object} caricoTable (per doctorId: { weekend, notti, rep, targetWeekendCount, targetWeekendNights, targetNightCountPerWeek, targetNightGapScore, ... }), totali e nMedici per popup
 */
export function computeCaricoTargetForPeriod(opts) {
  const {
    slotWEPeriodo = 0,
    weekendCountPeriodo = 0,
    weekendNightsPeriodo = 0,
    nottiPeriodo = 0,
    nottiFerialiPeriodo = 0,
    nottiWeeksPeriodo = 1,
    repPeriodo = 0,
    giorniPeriodo = 1,
    doctorIds = [],
    absenceDaysInPeriodByDoctor = {},
    weekendDaysInPeriod = 0,
    weekendDaysAbsentByDoctor = {},
    eligibleWeekendIds = [],
    eligibleNightIds = [],
    eligibleRepIds = [],
    lockedWeekendByDoctor = {},
    lockedWeekendCountByDoctor = {},
    lockedWeekendNightsByDoctor = {},
    lockedNightsByDoctor = {},
    lockedNightsFerialiByDoctor = {},
    lockedRepByDoctor = {},
  } = opts;
  const nMediciWE = Math.max(1, eligibleWeekendIds.length);
  const nMediciNotti = Math.max(1, eligibleNightIds.length);
  const nMediciRep = Math.max(1, eligibleRepIds.length);
  const setWE = new Set(eligibleWeekendIds);
  const setN = new Set(eligibleNightIds);
  const setR = new Set(eligibleRepIds);
  const weekendDaysTotal = Math.max(1, weekendDaysInPeriod || 1);
  const caricoTable = {};
  const rawWEByDoc = {};
  const rawWeekendCountByDoc = {};
  const rawWeekendNightsByDoc = {};
  for (const docId of doctorIds) {
    const assenze = absenceDaysInPeriodByDoctor[docId] ?? 0;
    const factorGen = Math.max(0, 1 - assenze / Math.max(1, giorniPeriodo));
    const weekendAbsent = weekendDaysAbsentByDoctor[docId] ?? 0;
    const factorWE = Math.max(0, 1 - weekendAbsent / weekendDaysTotal);
    const rawWE = setWE.has(docId) ? Math.round((slotWEPeriodo / nMediciWE) * factorWE) : 0;
    rawWEByDoc[docId] = rawWE;
    const rawWeekendCount = setWE.has(docId) ? Math.round((weekendCountPeriodo / nMediciWE) * factorWE) : 0;
    rawWeekendCountByDoc[docId] = rawWeekendCount;
    const rawWeekendNights = setN.has(docId) ? Math.round((weekendNightsPeriodo / nMediciNotti) * factorGen) : 0;
    rawWeekendNightsByDoc[docId] = rawWeekendNights;
    const rawNotti = setN.has(docId) ? Math.round((nottiPeriodo / nMediciNotti) * factorGen) : 0;
    const rawNottiFeriali = setN.has(docId) ? Math.round((nottiFerialiPeriodo / nMediciNotti) * factorGen) : 0;
    const rawRep = setR.has(docId) ? Math.round((repPeriodo / nMediciRep) * factorGen) : 0;
    const lockedWE = lockedWeekendByDoctor[docId] ?? 0;
    const lockedWECount = lockedWeekendCountByDoctor[docId] ?? 0;
    const lockedWENights = lockedWeekendNightsByDoctor[docId] ?? 0;
    const lockedN = lockedNightsByDoctor[docId] ?? 0;
    const lockedNFeriali = lockedNightsFerialiByDoctor[docId] ?? 0;
    const lockedR = lockedRepByDoctor[docId] ?? 0;
    const residualNightCount = Math.max(0, rawNottiFeriali - lockedNFeriali);
    const targetNightCountPerWeek = Math.max(0, Math.ceil(residualNightCount / Math.max(1, nottiWeeksPeriodo)));
    // Giorni medi tra due notti desiderati nel periodo (solo notti feriali): più alto => maggiore diluizione.
    const targetNightGapScore = residualNightCount > 0
      ? Math.max(1, Math.floor(giorniPeriodo / (residualNightCount + 1)))
      : 0;
    caricoTable[docId] = {
      weekend: Math.max(0, rawWE - lockedWE),
      targetWeekendCount: Math.max(0, rawWeekendCount - lockedWECount),
      targetWeekendNights: Math.max(0, rawWeekendNights - lockedWENights),
      notti: Math.max(0, rawNotti - lockedN),
      targetNightCountPerWeek,
      targetNightGapScore,
      rep: Math.max(0, rawRep - lockedR),
      rawWeekend: rawWE,
      rawWeekendCount,
      rawWeekendNights,
      rawNotti: rawNotti,
      rawNottiFeriali,
      rawRep: rawRep,
      lockedWeekend: lockedWE,
      lockedWeekendCount: lockedWECount,
      lockedWeekendNights: lockedWENights,
      lockedNotti: lockedN,
      lockedNottiFeriali: lockedNFeriali,
      lockedRep: lockedR,
      assenzeInPeriodo: assenze,
      weekendDaysAbsent: weekendAbsent,
      weekendFairnessEligible: setWE.has(docId) && factorWE > 0,
      nightFairnessEligible: setN.has(docId) && factorGen > 0,
    };
  }
  // Round-to-total: garantire sum(rawWE) = slotWEPeriodo
  let sumRawWE = Object.values(rawWEByDoc).reduce((a, b) => a + b, 0);
  if (sumRawWE !== slotWEPeriodo && slotWEPeriodo > 0) {
    const eligible = doctorIds.filter(id => setWE.has(id));
    const delta = slotWEPeriodo - sumRawWE;
    if (delta > 0) {
      // Aggiungere 1 a chi ha più factorWE (o meno locked)
      eligible.sort((a, b) => {
        const fa = Math.max(0, 1 - (weekendDaysAbsentByDoctor[a] ?? 0) / weekendDaysTotal);
        const fb = Math.max(0, 1 - (weekendDaysAbsentByDoctor[b] ?? 0) / weekendDaysTotal);
        if (fa !== fb) return fb - fa;
        return (lockedWeekendByDoctor[a] ?? 0) - (lockedWeekendByDoctor[b] ?? 0);
      });
      for (let i = 0; i < delta && i < eligible.length; i++) {
        const docId = eligible[i];
        rawWEByDoc[docId] = (rawWEByDoc[docId] ?? 0) + 1;
        caricoTable[docId].rawWeekend = rawWEByDoc[docId];
        caricoTable[docId].weekend = Math.max(0, rawWEByDoc[docId] - (caricoTable[docId].lockedWeekend ?? 0));
      }
    } else {
      // Togliere 1 da chi ha meno factorWE (o più locked)
      eligible.sort((a, b) => {
        const fa = Math.max(0, 1 - (weekendDaysAbsentByDoctor[a] ?? 0) / weekendDaysTotal);
        const fb = Math.max(0, 1 - (weekendDaysAbsentByDoctor[b] ?? 0) / weekendDaysTotal);
        if (fa !== fb) return fa - fb;
        return (lockedWeekendByDoctor[b] ?? 0) - (lockedWeekendByDoctor[a] ?? 0);
      });
      for (let i = 0; i < -delta && i < eligible.length; i++) {
        const docId = eligible[i];
        if ((rawWEByDoc[docId] ?? 0) > 0) {
          rawWEByDoc[docId] = rawWEByDoc[docId] - 1;
          caricoTable[docId].rawWeekend = rawWEByDoc[docId];
          caricoTable[docId].weekend = Math.max(0, rawWEByDoc[docId] - (caricoTable[docId].lockedWeekend ?? 0));
        }
      }
    }
  }
  // Round-to-total: garantire sum(rawWeekendCount) = weekendCountPeriodo
  let sumRawWeekendCount = Object.values(rawWeekendCountByDoc).reduce((a, b) => a + b, 0);
  if (sumRawWeekendCount !== weekendCountPeriodo && weekendCountPeriodo > 0) {
    const eligible = doctorIds.filter(id => setWE.has(id));
    const delta = weekendCountPeriodo - sumRawWeekendCount;
    if (delta > 0) {
      eligible.sort((a, b) => {
        const fa = Math.max(0, 1 - (weekendDaysAbsentByDoctor[a] ?? 0) / weekendDaysTotal);
        const fb = Math.max(0, 1 - (weekendDaysAbsentByDoctor[b] ?? 0) / weekendDaysTotal);
        if (fa !== fb) return fb - fa;
        return (lockedWeekendCountByDoctor[a] ?? 0) - (lockedWeekendCountByDoctor[b] ?? 0);
      });
      for (let i = 0; i < delta && i < eligible.length; i++) {
        const docId = eligible[i];
        rawWeekendCountByDoc[docId] = (rawWeekendCountByDoc[docId] ?? 0) + 1;
        caricoTable[docId].rawWeekendCount = rawWeekendCountByDoc[docId];
        caricoTable[docId].targetWeekendCount = Math.max(0, rawWeekendCountByDoc[docId] - (caricoTable[docId].lockedWeekendCount ?? 0));
      }
    } else {
      eligible.sort((a, b) => {
        const fa = Math.max(0, 1 - (weekendDaysAbsentByDoctor[a] ?? 0) / weekendDaysTotal);
        const fb = Math.max(0, 1 - (weekendDaysAbsentByDoctor[b] ?? 0) / weekendDaysTotal);
        if (fa !== fb) return fa - fb;
        return (lockedWeekendCountByDoctor[b] ?? 0) - (lockedWeekendCountByDoctor[a] ?? 0);
      });
      for (let i = 0; i < -delta && i < eligible.length; i++) {
        const docId = eligible[i];
        if ((rawWeekendCountByDoc[docId] ?? 0) > 0) {
          rawWeekendCountByDoc[docId] = rawWeekendCountByDoc[docId] - 1;
          caricoTable[docId].rawWeekendCount = rawWeekendCountByDoc[docId];
          caricoTable[docId].targetWeekendCount = Math.max(0, rawWeekendCountByDoc[docId] - (caricoTable[docId].lockedWeekendCount ?? 0));
        }
      }
    }
  }
  // Round-to-total: garantire sum(rawWeekendNights) = weekendNightsPeriodo
  let sumRawWeekendNights = Object.values(rawWeekendNightsByDoc).reduce((a, b) => a + b, 0);
  if (sumRawWeekendNights !== weekendNightsPeriodo && weekendNightsPeriodo > 0) {
    const eligibleN = doctorIds.filter(id => setN.has(id));
    const deltaN = weekendNightsPeriodo - sumRawWeekendNights;
    if (deltaN > 0) {
      eligibleN.sort((a, b) => {
        const aa = absenceDaysInPeriodByDoctor[a] ?? 0;
        const ab = absenceDaysInPeriodByDoctor[b] ?? 0;
        if (aa !== ab) return aa - ab;
        return (lockedWeekendNightsByDoctor[a] ?? 0) - (lockedWeekendNightsByDoctor[b] ?? 0);
      });
      for (let i = 0; i < deltaN && i < eligibleN.length; i++) {
        const docId = eligibleN[i];
        rawWeekendNightsByDoc[docId] = (rawWeekendNightsByDoc[docId] ?? 0) + 1;
        caricoTable[docId].rawWeekendNights = rawWeekendNightsByDoc[docId];
        caricoTable[docId].targetWeekendNights = Math.max(0, rawWeekendNightsByDoc[docId] - (caricoTable[docId].lockedWeekendNights ?? 0));
      }
    } else {
      eligibleN.sort((a, b) => {
        const aa = absenceDaysInPeriodByDoctor[a] ?? 0;
        const ab = absenceDaysInPeriodByDoctor[b] ?? 0;
        if (aa !== ab) return ab - aa;
        return (lockedWeekendNightsByDoctor[b] ?? 0) - (lockedWeekendNightsByDoctor[a] ?? 0);
      });
      for (let i = 0; i < -deltaN && i < eligibleN.length; i++) {
        const docId = eligibleN[i];
        if ((rawWeekendNightsByDoc[docId] ?? 0) > 0) {
          rawWeekendNightsByDoc[docId] = rawWeekendNightsByDoc[docId] - 1;
          caricoTable[docId].rawWeekendNights = rawWeekendNightsByDoc[docId];
          caricoTable[docId].targetWeekendNights = Math.max(0, rawWeekendNightsByDoc[docId] - (caricoTable[docId].lockedWeekendNights ?? 0));
        }
      }
    }
  }
  return {
    caricoTable,
    slotWEPeriodo,
    nottiPeriodo,
    repPeriodo,
    giorniPeriodo,
    nMediciWE,
    nMediciNotti,
    nMediciRep,
  };
}

/** Ore e giorni per medico per ogni settimana nell'intervallo [weekFrom, weekTo]. Ritorna { weekStart: { doctorId: { total, feriali, ..., days } } }.
 * Include la notte domenica precedente al primo lunedì (6h spill lun 0-8 nella prima settimana). */
export function computeWeeklyBreakdownForWeekRange(weekFrom, weekTo) {
  const dates = getDaysInWeekRange(weekFrom, weekTo).map(d => d.date);
  const prevSunday = addCalendarDays(weekFrom, -1);
  const slots = getOfficialSlotsForDates([prevSunday, ...dates]);
  return computeWeeklyBreakdownFromSlots(slots, null, null);
}

/** Aggrega il breakdown settimanale in totali per medico. weekStartsSorted = elenco ordinato di weekStart da includere. */
export function aggregateWorkloadFromWeeklyBreakdown(weeklyBreakdown, weekStartsSorted) {
  const aggregated = {};
  for (const weekStart of weekStartsSorted) {
    const byDoc = weeklyBreakdown[weekStart];
    if (!byDoc) continue;
    for (const [docId, w] of Object.entries(byDoc)) {
      if (!aggregated[docId]) aggregated[docId] = { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0, total: 0 };
      aggregated[docId].feriali += w.feriali ?? 0;
      aggregated[docId].weekend += w.weekend ?? 0;
      aggregated[docId].notturne += w.notturne ?? 0;
      aggregated[docId].reperibilita += w.reperibilita ?? 0;
      aggregated[docId].total += w.total ?? 0;
    }
  }
  return aggregated;
}

/** Numero slot e ore programmati (da coprire) per la settimana ISO (lun–dom). Esclude reperibilità e slot Direzione.
 *  Slot: M/P=1, Pr=1,2, N=2. Ogni unità di slot = HOURS_PER_SLOT (6h), quindi ore domanda = slot × 6.
 *  Usa getOfficialSlotsForDates (come Piano turni): calendario + DB + deduplica, niente doppi conteggi. */
function computeScheduledForWeek(weekStart) {
  const dates = getWeekDates(weekStart);
  const weekEnd = getWeekEnd(weekStart);
  const slots = getOfficialSlotsForDates(dates);
  let slotCount = 0;
  for (const slot of slots) {
    if (slot.date < weekStart || slot.date > weekEnd) continue;
    const isRep = slot.activityType === 'reperibilita' || slot.timeSlot === 'reperibilita_notte' || slot.timeSlot === 'reperibilita_giorno' || slot.timeSlot === 'reperibilita';
    if (isRep) continue;
    if (isDirezioneSlotForWorkload(slot)) continue;
    slotCount += preventiveWorkloadSlotWeight(slot);
  }
  return { hours: slotCount * HOURS_PER_SLOT, slots: slotCount };
}

/** Ore disponibilità per una settimana: capacità medici (workingDays × ore/giorno medico) meno ore perse per assenze giustificate (getHoursLostForAvailability). Esclude medici con rapporto già cessato (dataFineRapporto < lunedì settimana) e medici con Abilità Direzione (il loro monte non entra nella disponibilità clinica). */
function computeAvailabilityForWeek(weekStart) {
  const { doctors } = getState();
  const [year] = weekStart.split('-').map(Number);
  const festiviCount = getFestiviCountInWeek(weekStart, year);
  const workingDays = 5 - festiviCount;
  const weekEnd = getWeekEnd(weekStart);
  let availability = 0;
  for (const doc of doctors) {
    if (!doc?.id) continue;
    if (doc.abilitaDirezione) continue;
    const dataFine = doc.dataFineRapporto || null;
    if (dataFine && weekStart > String(dataFine).slice(0, 10)) continue;
    // Medici con monte orario settimanale 0 (o non valido) non offrono disponibilità clinica:
    // vanno esclusi dal calcolo (altrimenti il fallback a tempo pieno li conteggerebbe).
    const debitoSettimanale = Number(doc.debitoOrarioSettimanale);
    if (!Number.isFinite(debitoSettimanale) || debitoSettimanale <= 0) continue;
    const hoursPerDay = getHoursPerDayForDoctorOnDate(doc.id, weekStart);
    const capacity = workingDays * hoursPerDay;
    const hoursLost = getHoursLostForAvailability(doc.id, weekStart, weekEnd, hoursPerDay);
    availability += Math.max(0, capacity - hoursLost);
  }
  return availability;
}

/** Bilancio settimanale: ore da coprire, disponibilità e differenza. diff >= 0 = margine, diff < 0 = richiederà straordinari.
 *  Domanda (stima preventiva): M/P=1, Pr=1,2, N=2, Rep=0; slot Direzione esclusi; ore = slot × 6h. Slot disponibilità: ore capacità / HOURS_PER_SLOT (6h),
 *  allineato al conteggio contrattuale (es. 38h → ~6,33 slot; 36h lavorate → 6 slot). Medici con Abilità Direzione esclusi dalla disponibilità. */
export function computeWeeklyBalance(weekStart) {
  const { hours: scheduled, slots: scheduledSlots } = computeScheduledForWeek(weekStart);
  const availability = computeAvailabilityForWeek(weekStart);
  const diff = availability - scheduled;
  const availabilitySlots = availability / HOURS_PER_SLOT;
  const balanceSlots = availabilitySlots - scheduledSlots;
  return { scheduled, availability, diff, scheduledSlots, availabilitySlots, balanceSlots };
}

/** Bilancio per ogni settimana nell'intervallo [weekFrom, weekTo]. */
export function computeWeeklyBalanceForRange(weekFrom, weekTo) {
  const weekStarts = getWeekStartsInRange(weekFrom, weekTo);
  const result = {};
  for (const ws of weekStarts) {
    result[ws] = computeWeeklyBalance(ws);
  }
  return result;
}

/** Ore dovute per medico in un intervallo di settimane: per ogni settimana, (lun–ven non festivi) × ore/giorno da debito anagrafico (÷5). Esclude settimane dopo dataFineRapporto. */
export function computeExpectedHoursForWeekRange(weekStarts, doctors) {
  const result = {};
  for (const d of doctors) {
    const dataFine = d.dataFineRapporto || null;
    let expected = 0;
    for (const ws of weekStarts) {
      if (dataFine && ws > dataFine) continue;
      const hoursPerDay = getExpectedHoursPerFerialeDayForCarichi(d.id, ws);
      const [y] = ws.split('-').map(Number);
      const festiviCount = getFestiviCountInWeek(ws, y);
      const workingDays = Math.max(0, 5 - festiviCount);
      expected += workingDays * hoursPerDay;
    }
    result[d.id] = expected;
  }
  return result;
}

/** Lunedì prima e dopo settimana ISO che contengono il primo e l'ultimo giorno del mese solare. */
export function getIsoWeekRangeForCalendarMonth(year, month) {
  const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const monthEnd = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  const weekFrom = getWeekStart(monthStart);
  const weekTo = getWeekStart(monthEnd);
  return { weekFrom, weekTo, monthStart, monthEnd };
}

/** Ore dovute per medico in un mese solare: per ogni lun–ven non festivo, ore/giorno = debito settimanale ÷ 5. */
export function computeExpectedHoursForCalendarMonth(year, month, doctors) {
  const result = {};
  const lastDay = new Date(year, month, 0).getDate();
  const festivi = new Set(getFestiviInfrasettimanali(year));
  for (const d of doctors) {
    result[d.id] = 0;
    const dataFine = d.dataFineRapporto || null;
    for (let day = 1; day <= lastDay; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      if (dataFine && dateStr > dataFine) continue;
      const wd = new Date(dateStr + 'T12:00:00').getDay();
      if (wd === 0 || wd === 6) continue;
      if (festivi.has(dateStr)) continue;
      result[d.id] += getExpectedHoursPerFerialeDayForCarichi(d.id, dateStr);
    }
  }
  return result;
}

/**
 * Ore dovute per medico in un intervallo di date [startDate, endDate], estremi inclusi:
 * per ogni lun–ven non festivo, ore/giorno = debito settimanale ÷ 5.
 * Serve per fermare il conteggio a metà bimestre (traiettoria alla data del piano prodotto).
 */
export function computeExpectedHoursForDateRange(startDate, endDate, doctors) {
  const result = {};
  for (const d of doctors) result[d.id] = 0;
  if (!startDate || !endDate || startDate > endDate) return result;
  const festiviByYear = {};
  for (let ds = startDate; ds <= endDate; ds = addCalendarDays(ds, 1)) {
    const y = parseInt(ds.slice(0, 4), 10);
    if (!festiviByYear[y]) festiviByYear[y] = new Set(getFestiviInfrasettimanali(y));
    const wd = new Date(ds + 'T12:00:00').getDay();
    if (wd === 0 || wd === 6) continue;
    if (festiviByYear[y].has(ds)) continue;
    for (const d of doctors) {
      const dataFine = d.dataFineRapporto || null;
      if (dataFine && ds > dataFine) continue;
      result[d.id] += getExpectedHoursPerFerialeDayForCarichi(d.id, ds);
    }
  }
  return result;
}

// ===== Bimestre (Circolare ISS n. 6 del 3/7/2026: orario di lavoro flessibile su base bimestrale) =====
// Ore dovute: 7,6h (debito settimanale ÷ 5) per ogni giorno feriale lun–ven non festivo del bimestre.
// Ore coperte: turni assegnati + assenze accreditate (congedo ordinario, permessi, formazione, malattia),
// che per la circolare "coprono" il debito orario. Le reperibilità restano fuori dal monte orario.
// Attribuzione per data di calendario: la notte 20-8 vale 4h sul giorno di inizio e 8h sul successivo,
// quindi un turno a cavallo del confine di bimestre entra solo per la quota che ci ricade davvero.

const BIMESTER_LABELS = [
  'Gennaio–Febbraio',
  'Marzo–Aprile',
  'Maggio–Giugno',
  'Luglio–Agosto',
  'Settembre–Ottobre',
  'Novembre–Dicembre',
];

/** Indice bimestre (1–6) per un mese 1–12: 1 = gen-feb, … 5 = set-ott, 6 = nov-dic. */
export function getBimesterIndexForMonth(month) {
  const m = Math.min(12, Math.max(1, Number(month) || 1));
  return Math.floor((m - 1) / 2) + 1;
}

/** Chiave bimestre "YYYY-Bn" (es. 2026-B5 = settembre–ottobre 2026). */
export function getBimesterKey(year, index) {
  return `${year}-B${Math.min(6, Math.max(1, Number(index) || 1))}`;
}

/** Interpreta una chiave "YYYY-Bn". Ritorna { year, index } o null. */
export function parseBimesterKey(key) {
  const m = /^(\d{4})-B([1-6])$/.exec(String(key ?? '').trim());
  if (!m) return null;
  return { year: parseInt(m[1], 10), index: parseInt(m[2], 10) };
}

/** Etichetta leggibile del bimestre (es. "Settembre–Ottobre 2026"). */
export function getBimesterLabel(year, index) {
  const idx = Math.min(6, Math.max(1, Number(index) || 1));
  return `${BIMESTER_LABELS[idx - 1]} ${year}`;
}

/** Estremi del bimestre: { year, index, key, label, months: [m1, m2], start, end }. */
export function getBimesterRange(year, index) {
  const idx = Math.min(6, Math.max(1, Number(index) || 1));
  const m1 = idx * 2 - 1;
  const m2 = m1 + 1;
  const lastDay = new Date(year, m2, 0).getDate();
  return {
    year,
    index: idx,
    key: getBimesterKey(year, idx),
    label: getBimesterLabel(year, idx),
    months: [m1, m2],
    start: `${year}-${String(m1).padStart(2, '0')}-01`,
    end: `${year}-${String(m2).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
  };
}

/** Bimestre che contiene la data indicata. */
export function getBimesterForDate(dateStr) {
  const y = parseInt(String(dateStr ?? '').slice(0, 4), 10);
  const m = parseInt(String(dateStr ?? '').slice(5, 7), 10);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return null;
  return getBimesterRange(y, getBimesterIndexForMonth(m));
}

/** Bimestre precedente / successivo (con cambio anno). */
export function getAdjacentBimester(year, index, delta) {
  let idx = Math.min(6, Math.max(1, Number(index) || 1)) + Number(delta || 0);
  let y = Number(year);
  while (idx < 1) { idx += 6; y -= 1; }
  while (idx > 6) { idx -= 6; y += 1; }
  return getBimesterRange(y, idx);
}

/** Giorni feriali (lun–ven) non festivi in un intervallo di date, estremi inclusi. */
export function countFerialiInDateRange(startDate, endDate) {
  if (!startDate || !endDate || startDate > endDate) return 0;
  const festiviByYear = {};
  let count = 0;
  for (let d = startDate; d <= endDate; d = addCalendarDays(d, 1)) {
    const y = parseInt(d.slice(0, 4), 10);
    if (!festiviByYear[y]) festiviByYear[y] = getFestiviInfrasettimanali(y);
    const wd = new Date(d + 'T12:00:00').getDay();
    if (wd === 0 || wd === 6) continue;
    if (festiviByYear[y].has(d)) continue;
    count += 1;
  }
  return count;
}

/** Ore dovute nel bimestre per medico: somma dei due mesi solari (lun–ven non festivi × debito ÷ 5). */
export function computeExpectedHoursForBimester(year, index, doctors) {
  const { months } = getBimesterRange(year, index);
  const result = {};
  for (const d of doctors) result[d.id] = 0;
  for (const m of months) {
    const part = computeExpectedHoursForCalendarMonth(year, m, doctors);
    for (const d of doctors) result[d.id] = (result[d.id] ?? 0) + (part[d.id] ?? 0);
  }
  return result;
}

/**
 * Ore coperte per medico in un intervallo di date [startDate, endDate], estremi inclusi.
 * Attribuzione per data: notte 20-8 = 4h sul giorno di inizio + 8h sul giorno successivo; ogni quota
 * conta solo se la sua data cade nell'intervallo. Le reperibilità sono riportate a parte (fuori monte orario).
 * Consolidate = ore in settimane archiviate col lucchetto; provvisorie = piano ancora modificabile.
 * @returns {object} { doctorId: { turni, turniConsolidati, turniProvvisori, assenze, reperibilita, coperte } }
 */
export function computeCoveredHoursForDateRange(startDate, endDate) {
  const { doctors, shiftAssignmentsByKey } = getState();
  const byKey = shiftAssignmentsByKey || {};
  const assignmentByLogicalKey = buildWorkloadAssignmentByLogicalKey();
  const result = {};
  const ensure = (docId) => {
    if (!result[docId]) {
      result[docId] = {
        turni: 0, turniConsolidati: 0, turniProvvisori: 0,
        assenze: 0, reperibilita: 0, coperte: 0,
      };
    }
    return result[docId];
  };
  if (!startDate || !endDate || startDate > endDate) return result;

  // Il giorno precedente serve per la quota 0-8 di una notte iniziata prima dell'intervallo.
  const dayBefore = addCalendarDays(startDate, -1);
  const dates = [];
  for (let d = dayBefore; d <= endDate; d = addCalendarDays(d, 1)) dates.push(d);
  const slots = getOfficialSlotsForDates(dates);

  const weekLockCache = {};
  const isDateConsolidated = (dateStr) => {
    const ws = getWeekStart(dateStr);
    if (!(ws in weekLockCache)) weekLockCache[ws] = Boolean(isWeekLocked(ws));
    return weekLockCache[ws];
  };

  for (const slot of slots) {
    const docId = getDoctorIdAssignedToWorkloadSlot(slot, byKey, assignmentByLogicalKey);
    if (!docId) continue;
    const ds = (slot.date || '').slice(0, 10);
    if (!ds) continue;
    const rec = ensure(docId);
    const isRep = slot.activityType === 'reperibilita' || (slot.timeSlot || '').startsWith('reperibilita');
    if (isRep) {
      if (ds >= startDate && ds <= endDate) rec.reperibilita += slotHours(slot);
      continue;
    }
    for (const [partDate, partHours] of slotHourPartsByDate(slot)) {
      if (partHours <= 0) continue;
      if (partDate < startDate || partDate > endDate) continue;
      rec.turni += partHours;
      if (isDateConsolidated(partDate)) rec.turniConsolidati += partHours;
      else rec.turniProvvisori += partHours;
    }
  }

  for (const doc of doctors) {
    const rec = ensure(doc.id);
    rec.assenze = getCreditedAbsenceHoursForRange(doc.id, startDate, endDate) || 0;
    rec.coperte = rec.turni + rec.assenze;
  }
  return result;
}

/** Sotto questa frazione del dovuto settimanale, e senza assenze registrate, la settimana e'
 *  sospetta: quasi sempre e' un'assenza parziale non inserita. Vedi findWorkloadGaps. */
export const WORKLOAD_GAP_PARTIAL_RATIO = 0.5;

/**
 * Settimane ISO in cui un medico non ha ne' turni ne' assenze registrate, pur avendo un monte
 * orario da coprire. Sono quasi sempre assenze non inserite: gonfiano le ore dovute e, da quando
 * il saldo bimestrale alimenta il consolidamento, possono tradursi in una ritenuta su ore che il
 * medico non doveva.
 *
 * Non segnala le settimane in cui nessuno ha assegnazioni (piano non ancora generato): li' il
 * vuoto non dice nulla sul singolo medico. Esclude i medici con Abilita Direzione, il cui lavoro
 * non passa dal piano turni e che risulterebbero "vuoti" ogni settimana.
 *
 * Secondo livello (`partial*`): settimane in cui il medico ha lavorato meno della meta' del
 * proprio dovuto senza alcuna assenza registrata. Sono i buchi parziali — qualche giorno di
 * ferie non inserito dentro una settimana lavorata — che il primo livello non puo' vedere.
 * La soglia e' calibrata sui dati reali: su 142 settimane-medico senza assenze registrate,
 * al 50% segnala solo i casi veri, mentre al 70% inizia a segnalare settimane da quattro turni
 * che sono normali.
 *
 * @returns {{ byDoctor: Record<string, string[]>, weeks: string[], totalWeeks: number,
 *   partialByDoctor: Record<string, string[]>, partialWeeks: string[], totalPartialWeeks: number }}
 */
export function findWorkloadGaps(startDate, endDate, doctorsList) {
  const { doctors, shiftAssignmentsByKey } = getState();
  const docs = (doctorsList ?? doctors ?? []).filter(d => d?.id && !d.abilitaDirezione);
  const empty = {
    byDoctor: {}, weeks: [], totalWeeks: 0,
    partialByDoctor: {}, partialWeeks: [], totalPartialWeeks: 0,
  };
  if (!startDate || !endDate || startDate > endDate || docs.length === 0) return empty;

  const byKey = shiftAssignmentsByKey || {};
  const assignmentByLogicalKey = buildWorkloadAssignmentByLogicalKey();
  const weekStarts = getWeekStartsInRange(getWeekStart(startDate), getWeekStart(endDate));
  if (weekStarts.length === 0) return empty;

  // Un solo passaggio sugli slot del periodo: ore di turno per medico/settimana.
  // Il giorno prima serve per la quota 0-8 di una notte iniziata prima dell'intervallo.
  const dates = [addCalendarDays(weekStarts[0], -1)];
  for (let d = weekStarts[0]; d <= getWeekEnd(weekStarts[weekStarts.length - 1]); d = addCalendarDays(d, 1)) {
    dates.push(d);
  }
  const assignedByWeek = {};
  const weekHasAssignments = {};
  for (const slot of getOfficialSlotsForDates(dates)) {
    const ds = (slot.date || '').slice(0, 10);
    if (!ds) continue;
    const isRep = slot.activityType === 'reperibilita' || (slot.timeSlot || '').startsWith('reperibilita');
    if (isRep) continue;
    const docId = getDoctorIdAssignedToWorkloadSlot(slot, byKey, assignmentByLogicalKey);
    if (!docId) continue;
    // Una settimana e' "pianificata" se ci sono turni che iniziano in essa: la sola coda 0-8
    // di una notte del sabato/domenica precedente non basta, altrimenti la prima settimana non
    // ancora pianificata risulterebbe piena di buchi per l'intero reparto.
    weekHasAssignments[getWeekStart(ds)] = true;
    // Le ore invece si attribuiscono per data: la coda della notte di domenica appartiene alla
    // settimana successiva, e puo' essere l'unica attivita' del medico in quella settimana.
    for (const [partDate, partHours] of slotHourPartsByDate(slot)) {
      if (partHours <= 0) continue;
      const ws = getWeekStart(partDate);
      if (!assignedByWeek[ws]) assignedByWeek[ws] = {};
      assignedByWeek[ws][docId] = (assignedByWeek[ws][docId] || 0) + partHours;
    }
  }

  const byDoctor = {};
  const weeks = new Set();
  const partialByDoctor = {};
  const partialWeeks = new Set();
  for (const ws of weekStarts) {
    if (!weekHasAssignments[ws]) continue; // piano non generato: nessun giudizio
    const weekEnd = getWeekEnd(ws);
    const [year] = ws.split('-').map(Number);
    const workingDays = Math.max(0, 5 - getFestiviCountInWeek(ws, year));
    if (workingDays === 0) continue;
    for (const doc of docs) {
      const dataFine = doc.dataFineRapporto ? String(doc.dataFineRapporto).slice(0, 10) : null;
      if (dataFine && ws > dataFine) continue;
      const hoursPerDay = getHoursPerDayForDoctorOnDate(doc.id, ws);
      if (!(workingDays * hoursPerDay > 0)) continue; // nessun monte ore da coprire
      const worked = assignedByWeek[ws]?.[doc.id] || 0;
      // Un'assenza registrata spiega gia' le ore mancanti: nessun sospetto, a nessun livello.
      const credited = getCreditedAbsenceHoursForRange(doc.id, ws, weekEnd) || 0;
      if (credited > 0) continue;
      if (worked <= 0) {
        if (!byDoctor[doc.id]) byDoctor[doc.id] = [];
        byDoctor[doc.id].push(ws);
        weeks.add(ws);
        continue;
      }
      if (worked < WORKLOAD_GAP_PARTIAL_RATIO * workingDays * hoursPerDay) {
        if (!partialByDoctor[doc.id]) partialByDoctor[doc.id] = [];
        partialByDoctor[doc.id].push(ws);
        partialWeeks.add(ws);
      }
    }
  }

  return {
    byDoctor,
    weeks: [...weeks].sort(),
    totalWeeks: Object.values(byDoctor).reduce((a, list) => a + list.length, 0),
    partialByDoctor,
    partialWeeks: [...partialWeeks].sort(),
    totalPartialWeeks: Object.values(partialByDoctor).reduce((a, list) => a + list.length, 0),
  };
}

/**
 * Ultimo giorno del periodo fino a cui il piano turni è stato effettivamente prodotto: fine
 * dell'ultima settimana ISO con assegnazioni, troncata alla fine del periodo.
 *
 * Serve a confrontare le ore coperte con quelle dovute *fino a lì*: sul bimestre intero le
 * settimane ancora da redigere fanno risultare tutti in debito, e il saldo di fine bimestre non
 * dice se il medico è allineato adesso.
 *
 * Una settimana conta come pianificata se ci sono turni (non reperibilità) che iniziano in essa,
 * stessa regola di findWorkloadGaps: la sola coda 0-8 di una notte della settimana prima non basta,
 * e le reperibilità — spesso generate con mesi di anticipo — non spostano la frontiera.
 *
 * @returns {string|null} data YYYY-MM-DD, o null se nel periodo non c'è ancora nessun turno assegnato.
 */
export function getPlannedThroughDate(startDate, endDate) {
  if (!startDate || !endDate || startDate > endDate) return null;
  const { shiftAssignmentsByKey } = getState();
  const byKey = shiftAssignmentsByKey || {};
  const assignmentByLogicalKey = buildWorkloadAssignmentByLogicalKey();
  const dates = [];
  for (let d = startDate; d <= endDate; d = addCalendarDays(d, 1)) dates.push(d);
  let lastWeekStart = null;
  for (const slot of getOfficialSlotsForDates(dates)) {
    const ds = (slot.date || '').slice(0, 10);
    if (!ds || ds < startDate || ds > endDate) continue;
    const isRep = slot.activityType === 'reperibilita' || (slot.timeSlot || '').startsWith('reperibilita');
    if (isRep) continue;
    if (!getDoctorIdAssignedToWorkloadSlot(slot, byKey, assignmentByLogicalKey)) continue;
    const ws = getWeekStart(ds);
    if (!lastWeekStart || ws > lastWeekStart) lastWeekStart = ws;
  }
  if (!lastWeekStart) return null;
  const weekEnd = getWeekEnd(lastWeekStart);
  return weekEnd < endDate ? weekEnd : endDate;
}

/**
 * Saldo bimestrale cumulato a una serie di date di taglio (tipicamente la domenica di ogni
 * settimana ISO mostrata nel Piano turni).
 *
 * Per ogni taglio: coperte(inizio bimestre → taglio) + riporto − dovute(inizio bimestre → taglio).
 * È lo stesso conto del saldo di Carichi, fermato però alla settimana che si sta guardando: dice
 * se il medico, *a quel punto del bimestre*, è avanti o indietro sulla traiettoria.
 *
 * Il bimestre è quello che contiene il taglio: chi chiama deve già aver troncato il taglio alla
 * fine del bimestre della settimana, così una settimana a cavallo non sconfina nel bimestre dopo.
 *
 * Una sola passata sugli slot per bimestre, perché la card del Piano si ricalcola a ogni
 * assegnazione: leggere due mesi di slot una volta per settimana visibile costerebbe troppo.
 *
 * @returns {object} { [cutoff]: { range, byDoctor: { [doctorId]: { dovute, turni, assenze,
 *   coperte, saldoApertura, saldo } } } }
 */
export function computeBimesterBalancesAtCutoffs(cutoffDates, doctorsList) {
  const { doctors, shiftAssignmentsByKey } = getState();
  const docs = doctorsList ?? doctors ?? [];
  const result = {};
  const cutoffs = [...new Set((cutoffDates || []).map(d => String(d || '').slice(0, 10)).filter(Boolean))].sort();
  if (cutoffs.length === 0 || docs.length === 0) return result;

  const byBimester = new Map();
  for (const cutoff of cutoffs) {
    const range = getBimesterForDate(cutoff);
    if (!range) continue;
    if (!byBimester.has(range.key)) byBimester.set(range.key, { range, cutoffs: [] });
    byBimester.get(range.key).cutoffs.push(cutoff);
  }

  const byKey = shiftAssignmentsByKey || {};
  const assignmentByLogicalKey = buildWorkloadAssignmentByLogicalKey();

  for (const { range, cutoffs: bimesterCutoffs } of byBimester.values()) {
    const lastCutoff = bimesterCutoffs[bimesterCutoffs.length - 1];
    // Il giorno prima serve per la quota 0-8 di una notte iniziata prima del bimestre.
    const dates = [addCalendarDays(range.start, -1)];
    for (let d = range.start; d <= lastCutoff; d = addCalendarDays(d, 1)) dates.push(d);

    // Quote di turno per data: una passata sola, poi somme progressive fino a ogni taglio.
    const parts = [];
    for (const slot of getOfficialSlotsForDates(dates)) {
      const docId = getDoctorIdAssignedToWorkloadSlot(slot, byKey, assignmentByLogicalKey);
      if (!docId) continue;
      const isRep = slot.activityType === 'reperibilita' || (slot.timeSlot || '').startsWith('reperibilita');
      if (isRep) continue;
      for (const [partDate, partHours] of slotHourPartsByDate(slot)) {
        if (partHours <= 0) continue;
        if (partDate < range.start || partDate > lastCutoff) continue;
        parts.push({ date: partDate, docId, hours: partHours });
      }
    }
    parts.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

    const turniByDoctor = {};
    let idx = 0;
    for (const cutoff of bimesterCutoffs) {
      while (idx < parts.length && parts[idx].date <= cutoff) {
        const { docId, hours } = parts[idx];
        turniByDoctor[docId] = (turniByDoctor[docId] || 0) + hours;
        idx += 1;
      }
      const expected = computeExpectedHoursForDateRange(range.start, cutoff, docs);
      const byDoctor = {};
      for (const d of docs) {
        const turni = turniByDoctor[d.id] || 0;
        const assenze = getCreditedAbsenceHoursForRange(d.id, range.start, cutoff) || 0;
        const coperte = turni + assenze;
        const dovute = expected[d.id] ?? 0;
        const saldoApertura = getOpeningBalanceForBimester(d, range.key);
        byDoctor[d.id] = {
          dovute,
          turni,
          assenze,
          coperte,
          saldoApertura,
          saldo: coperte + saldoApertura - dovute,
        };
      }
      result[cutoff] = { range, byDoctor };
    }
  }
  return result;
}

/** Scarto minimo perché la leva di preferenza scatti: sotto un turno intero, correggere di un
 *  turno sarebbe più sbagliato che non correggere. */
export const BIMESTER_BIAS_MIN_HOURS = HOURS_PER_SLOT;

/**
 * Saldo bimestrale che il medico si porta *in ingresso* a una settimana, calcolato sulle sole
 * settimane ISO **archiviate** (lucchetto) del bimestre che la precedono.
 *
 * Solo le settimane archiviate perché è lì che i dati sono stati verificati: un'assenza non ancora
 * registrata gonfierebbe le ore dovute e farebbe risultare in debito un medico che era in ferie.
 * Le settimane aperte non contano né in positivo né in negativo — restano neutre.
 *
 * Le settimane a cavallo del bimestre entrano solo per i giorni che ci ricadono davvero, e ogni
 * bimestre riparte da zero: un debito già chiuso (ed eventualmente trattenuto in busta) non si
 * trascina.
 *
 * @returns {object} { [weekStart]: { range, byDoctor: { [doctorId]: { dovute, turni, assenze,
 *   coperte, saldoApertura, saldo, settimaneArchiviate } } } }
 */
export function computeLockedBimesterBalancesBeforeWeeks(weekStarts, doctorsList) {
  const { doctors, shiftAssignmentsByKey } = getState();
  const docs = doctorsList ?? doctors ?? [];
  const result = {};
  const targets = [...new Set((weekStarts || []).map(w => String(w || '').slice(0, 10)).filter(Boolean))].sort();
  if (targets.length === 0 || docs.length === 0) return result;

  const byBimester = new Map();
  for (const ws of targets) {
    const range = getBimesterForDate(ws);
    if (!range) continue;
    if (!byBimester.has(range.key)) byBimester.set(range.key, { range, targets: [] });
    byBimester.get(range.key).targets.push(ws);
  }

  const byKey = shiftAssignmentsByKey || {};
  const assignmentByLogicalKey = buildWorkloadAssignmentByLogicalKey();

  for (const { range, targets: bimesterTargets } of byBimester.values()) {
    const lastTarget = bimesterTargets[bimesterTargets.length - 1];
    // Settimane ISO del bimestre che precedono l'ultima settimana richiesta.
    const weeks = getWeekStartsInRange(getWeekStart(range.start), lastTarget).filter(ws => ws < lastTarget);
    const lockedWeeks = weeks.filter(ws => isWeekLocked(ws));

    // Ore di turno per settimana archiviata, contando solo i giorni dentro il bimestre.
    const turniByWeek = {};
    if (lockedWeeks.length) {
      const spanStart = lockedWeeks[0];
      const spanEnd = getWeekEnd(lockedWeeks[lockedWeeks.length - 1]);
      const dates = [addCalendarDays(spanStart, -1)];
      for (let d = spanStart; d <= spanEnd; d = addCalendarDays(d, 1)) dates.push(d);
      const lockedSet = new Set(lockedWeeks);
      for (const slot of getOfficialSlotsForDates(dates)) {
        const docId = getDoctorIdAssignedToWorkloadSlot(slot, byKey, assignmentByLogicalKey);
        if (!docId) continue;
        const isRep = slot.activityType === 'reperibilita' || (slot.timeSlot || '').startsWith('reperibilita');
        if (isRep) continue;
        for (const [partDate, partHours] of slotHourPartsByDate(slot)) {
          if (partHours <= 0) continue;
          if (partDate < range.start || partDate > range.end) continue;
          const ws = getWeekStart(partDate);
          if (!lockedSet.has(ws)) continue;
          if (!turniByWeek[ws]) turniByWeek[ws] = {};
          turniByWeek[ws][docId] = (turniByWeek[ws][docId] || 0) + partHours;
        }
      }
    }

    // Dovute e assenze per settimana archiviata, sempre ritagliate sul bimestre.
    const dovuteByWeek = {};
    const assenzeByWeek = {};
    for (const ws of lockedWeeks) {
      const from = ws < range.start ? range.start : ws;
      const to = (() => {
        const weekEnd = getWeekEnd(ws);
        return weekEnd > range.end ? range.end : weekEnd;
      })();
      dovuteByWeek[ws] = computeExpectedHoursForDateRange(from, to, docs);
      assenzeByWeek[ws] = {};
      for (const d of docs) {
        assenzeByWeek[ws][d.id] = getCreditedAbsenceHoursForRange(d.id, from, to) || 0;
      }
    }

    // Somme progressive: per ogni settimana richiesta contano solo le archiviate che la precedono.
    for (const target of bimesterTargets) {
      const before = lockedWeeks.filter(ws => ws < target);
      const byDoctor = {};
      for (const d of docs) {
        let turni = 0;
        let assenze = 0;
        let dovute = 0;
        for (const ws of before) {
          turni += turniByWeek[ws]?.[d.id] || 0;
          assenze += assenzeByWeek[ws]?.[d.id] || 0;
          dovute += dovuteByWeek[ws]?.[d.id] ?? 0;
        }
        const coperte = turni + assenze;
        const saldoApertura = getOpeningBalanceForBimester(d, range.key);
        byDoctor[d.id] = {
          dovute,
          turni,
          assenze,
          coperte,
          saldoApertura,
          saldo: before.length ? coperte + saldoApertura - dovute : 0,
          settimaneArchiviate: before.length,
        };
      }
      result[target] = { range, byDoctor };
    }
  }
  return result;
}

/**
 * Leva di preferenza per il solver: +1 se il medico entra nella settimana sotto la traiettoria del
 * bimestre (merita un turno in più), −1 se ci entra sopra (un turno in meno), 0 altrimenti.
 * Vale ±1 e basta: la correzione non deve mai valere più di un turno.
 */
export function bimesterPreferenceBias(saldo, dovute) {
  if (!(dovute > 0)) return 0;
  if (saldo <= -BIMESTER_BIAS_MIN_HOURS) return 1;
  if (saldo >= BIMESTER_BIAS_MIN_HOURS) return -1;
  return 0;
}

/** Saldo di apertura applicabile al bimestre: vale solo per il bimestre indicato in anagrafica. */
export function getOpeningBalanceForBimester(doctor, bimesterKey) {
  if (!doctor) return 0;
  const ref = String(doctor.saldoOreAperturaBimestre ?? '').trim();
  if (!ref || ref !== bimesterKey) return 0;
  const v = Number(doctor.saldoOreApertura);
  return Number.isFinite(v) ? v : 0;
}

/**
 * Specchio bimestrale: per ogni medico ore dovute, coperte, saldo e scostamento dalla traiettoria.
 * saldo = ore coperte + saldo di apertura − ore dovute. Negativo = debito (ore ancora da assegnare).
 * scostamentoAlPiano = stesso conto fermato a `plannedThrough`, cioè fino a dove i turni sono stati
 * prodotti: dice se il medico è allineato adesso, senza contare le settimane ancora da redigere.
 * @returns {object} { range, plannedThrough, byDoctor: { doctorId: { dovute, turni, turniConsolidati,
 *   turniProvvisori, assenze, reperibilita, coperte, saldoApertura, saldo, oreMancanti, oreEsubero,
 *   dovuteAlPiano, coperteAlPiano, scostamentoAlPiano } } }
 */
export function computeBimesterMirror(year, index, doctorsList) {
  const { doctors } = getState();
  const docs = doctorsList ?? doctors ?? [];
  const range = getBimesterRange(year, index);
  const expected = computeExpectedHoursForBimester(range.year, range.index, docs);
  const covered = computeCoveredHoursForDateRange(range.start, range.end);

  // Traiettoria: stesso conto fermato alla data fino a cui il piano è stato prodotto. Sul bimestre
  // intero le settimane non ancora redatte fanno sembrare tutti in debito; qui si vede lo scarto vero.
  const plannedThrough = getPlannedThroughDate(range.start, range.end);
  const isFullRange = plannedThrough === range.end;
  const expectedToPlan = plannedThrough
    ? (isFullRange ? expected : computeExpectedHoursForDateRange(range.start, plannedThrough, docs))
    : null;
  const coveredToPlan = plannedThrough
    ? (isFullRange ? covered : computeCoveredHoursForDateRange(range.start, plannedThrough))
    : null;

  const byDoctor = {};
  for (const d of docs) {
    const c = covered[d.id] || {
      turni: 0, turniConsolidati: 0, turniProvvisori: 0,
      assenze: 0, reperibilita: 0, coperte: 0,
    };
    const dovute = expected[d.id] ?? 0;
    const saldoApertura = getOpeningBalanceForBimester(d, range.key);
    const saldo = c.coperte + saldoApertura - dovute;
    const dovuteAlPiano = plannedThrough ? (expectedToPlan[d.id] ?? 0) : 0;
    const coperteAlPiano = plannedThrough ? (coveredToPlan[d.id]?.coperte ?? 0) : 0;
    // Il riporto è debito del bimestre come gli altri: incluso, a fine bimestre lo scostamento
    // coincide con il saldo.
    const scostamentoAlPiano = plannedThrough ? coperteAlPiano + saldoApertura - dovuteAlPiano : 0;
    byDoctor[d.id] = {
      ...c,
      dovute,
      saldoApertura,
      saldo,
      oreMancanti: saldo < 0 ? -saldo : 0,
      oreEsubero: saldo > 0 ? saldo : 0,
      dovuteAlPiano,
      coperteAlPiano,
      scostamentoAlPiano,
    };
  }
  return { range, plannedThrough, byDoctor };
}

/** Chiavi YYYY-MM dei mesi solari che intersecano la settimana ISO (lun–dom) a partire da weekStart. */
export function getCalendarMonthKeysTouchingWeek(weekStart) {
  const keys = new Set();
  for (let i = 0; i < 7; i++) {
    const ds = addCalendarDays(weekStart, i);
    keys.add(ds.slice(0, 7));
  }
  return [...keys];
}

function isWorkloadHistoryMonthDataEmpty(data) {
  if (!data || typeof data !== 'object') return true;
  const vals = Object.values(data);
  if (vals.length === 0) return true;
  return vals.every(w =>
    !w ||
    ((w.total ?? 0) === 0 &&
      (w.feriali ?? 0) === 0 &&
      (w.weekend ?? 0) === 0 &&
      (w.notturne ?? 0) === 0 &&
      (w.reperibilita ?? 0) === 0)
  );
}

/**
 * Aggiorna lo storico carichi (chiave YYYY-MM) aggregando solo settimane archiviate nel range ISO del mese.
 * Rimuove la riga su stato e backend se non resta nessuna settimana archiviata nel mese o i totali sono tutti zero.
 */
export async function syncWorkloadHistoryForCalendarMonth(monthYm) {
  if (!/^\d{4}-\d{2}$/.test(monthYm || '')) return;
  const y = parseInt(monthYm.slice(0, 4), 10);
  const m = parseInt(monthYm.slice(5, 7), 10);
  const { weekFrom, weekTo } = getIsoWeekRangeForCalendarMonth(y, m);
  const weekStartsSorted = getWeekStartsInRange(weekFrom, weekTo);
  const lockedWeeks = weekStartsSorted.filter(ws => isWeekLocked(ws));
  if (lockedWeeks.length === 0) {
    await deleteWorkloadMonthFromHistory(monthYm);
    return;
  }
  const weeklyBreakdown = computeWeeklyBreakdownForWeekRange(weekFrom, weekTo);
  const workload = aggregateWorkloadFromWeeklyBreakdown(weeklyBreakdown, lockedWeeks);
  if (isWorkloadHistoryMonthDataEmpty(workload)) {
    await deleteWorkloadMonthFromHistory(monthYm);
    return;
  }
  await saveWorkloadToHistory(monthYm, workload);
}

/** Dopo blocco o sblocco settimana: ricalcola lo storico per ogni mese solare toccato dalla settimana. */
export async function syncWorkloadHistoryAfterWeekLockChange(weekStart) {
  const months = getCalendarMonthKeysTouchingWeek(weekStart);
  for (const ym of months) {
    await syncWorkloadHistoryForCalendarMonth(ym);
  }
}

/** Restituisce tutte le settimane ISO (lunedì YYYY-MM-DD) nell'anno. */
function getWeekStartsInYear(year) {
  const weekStarts = new Set();
  for (let month = 1; month <= 12; month++) {
    const daysInMonth = new Date(year, month, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      weekStarts.add(getWeekStart(dateStr));
    }
  }
  return [...weekStarts].sort();
}

/** Carico annuale per medico: somma delle ore per ogni settimana ISO nell'anno solare che risulta archiviata (lucchetto),
 * ricalcolata sul calendario e le assegnazioni **correnti** (una sola volta per `weekStart`, anche se più mesi nello storico
 * salvato ricadono sulla stessa settimana nel loro intervallo ISO).
 * Non somma le righe `workloadHistory` per mese: sommarle può duplicare le ore delle settimane al confine tra due mesi.
 * `weeksWithData` sono **solo** le settimane con `isWeekLocked` nell'anno (non “solo se c’è almeno un turno”).
 * Ritorna { workload, weeksCount, weeksWithData, expectedHoursByDoctor }. */
export function computeAnnualWorkloadFromAssignments(year) {
  const { doctors } = getState();
  const annualTotal = {};
  for (const d of doctors) {
    annualTotal[d.id] = { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0, total: 0 };
  }

  const allWeeksInYear = getWeekStartsInYear(year);
  const weeksList = allWeeksInYear.filter(ws => isWeekLocked(ws)).sort();

  if (weeksList.length > 0) {
    const rangeStart = addCalendarDays(weeksList[0], -1);
    const rangeEnd = getWeekEnd(weeksList[weeksList.length - 1]);
    const allDates = [];
    for (let ds = rangeStart; ds <= rangeEnd; ds = addCalendarDays(ds, 1)) {
      allDates.push(ds);
    }
    const slots = getOfficialSlotsForDates(allDates);
    const fullBreakdown = computeWeeklyBreakdownFromSlots(slots, null, null);
    for (const weekStart of weeksList) {
      const weekData = fullBreakdown[weekStart];
      if (!weekData) continue;
      for (const [docId, w] of Object.entries(weekData)) {
        if (!annualTotal[docId]) continue;
        annualTotal[docId].feriali += w.feriali ?? 0;
        annualTotal[docId].weekend += w.weekend ?? 0;
        annualTotal[docId].notturne += w.notturne ?? 0;
        annualTotal[docId].reperibilita += w.reperibilita ?? 0;
        annualTotal[docId].total += w.total ?? 0;
      }
    }
  }
  const expectedHoursByDoctor = {};
  for (const d of doctors) {
    const dataFine = d.dataFineRapporto || null;
    let expected = 0;
    for (const ws of weeksList) {
      if (dataFine && ws > dataFine) continue; // Escludi settimane dopo la cessazione del rapporto
      const hoursPerDay = getExpectedHoursPerFerialeDayForCarichi(d.id, ws);
      const [y] = ws.split('-').map(Number);
      const festiviCount = getFestiviCountInWeek(ws, y);
      const workingDays = Math.max(0, 5 - festiviCount);
      expected += workingDays * hoursPerDay;
    }
    expectedHoursByDoctor[d.id] = expected;
  }

  return { workload: annualTotal, weeksCount: weeksList.length, weeksWithData: weeksList, expectedHoursByDoctor };
}

/** Deficit annuale per medico: expected - actual. Positivo = deficit (da recuperare), negativo = esubero.
 *  expected = somma per ogni settimana con dati di (giorni lavorativi × ore/giorno da debito anagrafico ÷ 5).
 *  Ritorna { doctorId: { totalHours, expectedHours, deficit, weeksCount } }. */
export function computeAnnualDeficitByDoctor(year) {
  const { workload, weeksCount, expectedHoursByDoctor } = computeAnnualWorkloadFromAssignments(year);
  const { doctors } = getState();
  const result = {};
  for (const d of doctors) {
    const w = workload[d.id] || { total: 0 };
    const expectedHours = expectedHoursByDoctor[d.id] ?? (d.debitoOrarioSettimanale ?? DEFAULT_WEEKLY_HOURS) * weeksCount;
    const totalHours = w.total ?? 0;
    const deficit = Math.max(0, expectedHours - totalHours);
    result[d.id] = { totalHours, expectedHours, deficit, weeksCount };
  }
  return result;
}

/** Conteggio slot per medico per settimana ISO: somma di slot.slotWeight (M/P=1, Pr=1,2, N=2; rep=0).
 * Ritorna { doctorId: { isoWeek: total } } con isoWeek in formato "YYYY-Wnn". Notti domenica: split 50/50 tra settimana uscente e successiva.
 * Usato da PDF e da qualsiasi UI che mostra conteggi slot (Piano Turni, Carichi). */
export function computeSlotCountByWeek(slots, assignmentsByKey) {
  const assignmentByLogicalKey = buildWorkloadAssignmentByLogicalKey();
  const byDoc = {};
  for (const slot of slots) {
    const weight = slot.slotWeight ?? 0;
    if (weight === 0) continue;
    const docId = getDoctorIdAssignedToWorkloadSlot(slot, assignmentsByKey, assignmentByLogicalKey);
    if (!docId) continue;
    if (!byDoc[docId]) byDoc[docId] = {};
    const isSunday = slot.weekday === 'dom';
    const isNightSpan = slot.timeSlot === 'notte' && isSunday;
    if (isNightSpan) {
      const weekOut = getISOWeekString(slot.date);
      const nextDay = addCalendarDays(slot.date, 1);
      const weekIn = getISOWeekString(nextDay);
      byDoc[docId][weekOut] = (byDoc[docId][weekOut] || 0) + weight / 2;
      byDoc[docId][weekIn] = (byDoc[docId][weekIn] || 0) + weight / 2;
    } else {
      const week = getISOWeekString(slot.date);
      byDoc[docId][week] = (byDoc[docId][week] || 0) + weight;
    }
  }
  return byDoc;
}
